import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useCallback } from 'react';
import { useTheme } from '../../../../theme';
const TEAL_COLOR = '#4FC4C4';
const TEAL_BG = 'rgba(79, 196, 196, 0.18)';
const TEAL_BORDER = 'rgba(79, 196, 196, 0.45)';
const GLASS_BG = 'rgba(255, 255, 255, 0.06)';
const GLASS_BORDER = 'rgba(255, 255, 255, 0.18)';
const BASE_CLASSES = [
    'inline-flex items-center justify-center',
    'rounded-full border',
    'min-h-[44px] px-4 py-2',
    'text-sm font-medium',
    'cursor-pointer select-none',
    'backdrop-blur-[14px]',
    'transition-colors duration-150 motion-reduce:transition-none',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
].join(' ');
/**
 * `<VibePill>` — togglable pill chip used in the Search empty state vibe grid.
 *
 * Unselected: glass white-06 background, 1px white-18 border, backdrop blur.
 * Selected: teal-tinted fill with teal text and teal border. Activates on
 * click and on Enter/Space when `onSelect` is provided. Sets
 * `role="button"` + `aria-pressed` so assistive tech can announce the
 * selection state.
 */
export const VibePill = forwardRef(({ selected = false, onSelect, onClick, onKeyDown, children, className, style, tabIndex, ...rest }, ref) => {
    const theme = useTheme();
    const pillStyles = {
        fontFamily: theme.typography.fontFamily,
        backgroundColor: selected ? TEAL_BG : GLASS_BG,
        color: selected ? TEAL_COLOR : theme.colors.text,
        borderColor: selected ? TEAL_BORDER : GLASS_BORDER,
        '--tw-ring-color': theme.colors.ring,
        ...style,
    };
    const handleClick = useCallback((event) => {
        onClick?.(event);
        if (!event.defaultPrevented) {
            onSelect?.();
        }
    }, [onClick, onSelect]);
    const handleKeyDown = useCallback((event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented)
            return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect?.();
        }
    }, [onKeyDown, onSelect]);
    const classes = [BASE_CLASSES, className].filter(Boolean).join(' ');
    return (_jsx("span", { ref: ref, role: "button", "aria-pressed": selected, tabIndex: tabIndex ?? 0, className: classes, style: pillStyles, onClick: handleClick, onKeyDown: handleKeyDown, ...rest, children: children }));
});
VibePill.displayName = 'VibePill';
